// Knowledge Base Page - With tag filtering via URL
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Grid,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { Search, Article as ArticleIcon, LocalOffer, ChevronRight } from '@mui/icons-material';
import { usePortal } from './PortalLayout';
import { articlesService } from '@/services/collections/articles';
import type { Article } from '@/types';

export const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization, project } = usePortal();

  const [articles, setArticles] = useState<Article[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Get filter from URL
  const tagFilter = searchParams.get('tag');
  const categoryFilter = searchParams.get('category');

  // Fetch articles
  useEffect(() => {
    const fetchArticles = async () => {
      if (!organization || !project) return;

      setLoading(true);
      try {
        let data: Article[];

        if (categoryFilter) {
          data = await articlesService.getByCategory(organization.id, project.id, categoryFilter);
        } else {
          data = await articlesService.getPublished(organization.id, project.id);
        }

        // Filter by tag if present
        if (tagFilter) {
          data = data.filter((article) =>
            article.tags?.some((tag) => tag.toLowerCase() === tagFilter.toLowerCase())
          );
        }

        setArticles(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((article) => {
          article.tags?.forEach((tag) => tags.add(tag));
        });
        setAllTags(Array.from(tags).sort());
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [organization, project, tagFilter, categoryFilter]);

  // Filter by search
  const filteredArticles = articles.filter((article) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      article.title.toLowerCase().includes(query) ||
      article.content.toLowerCase().includes(query) ||
      article.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  // Group articles by category
  const articlesByCategory = filteredArticles.reduce((acc, article) => {
    const cat = article.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(article);
    return acc;
  }, {} as Record<string, Article[]>);

  // Handle tag click
  const handleTagClick = (tag: string) => {
    setSearchParams({ tag });
  };

  // Clear filters
  const clearFilters = () => {
    setSearchParams({});
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/p/${organization?.slug}`);
          }}
        >
          Home
        </Link>
        <Typography color="text.primary">Knowledge Base</Typography>
        {tagFilter && <Typography color="text.primary">Tag: {tagFilter}</Typography>}
        {categoryFilter && <Typography color="text.primary">{categoryFilter}</Typography>}
      </Breadcrumbs>

      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Knowledge Base
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Find answers to common questions and learn how to use our product
      </Typography>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search articles..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 500 }}
      />

      {/* Active Filters */}
      {(tagFilter || categoryFilter) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filters:
          </Typography>
          <Stack direction="row" spacing={1}>
            {tagFilter && (
              <Chip
                icon={<LocalOffer />}
                label={`Tag: ${tagFilter}`}
                onDelete={clearFilters}
                color="primary"
              />
            )}
            {categoryFilter && (
              <Chip
                label={`Category: ${categoryFilter}`}
                onDelete={clearFilters}
                color="primary"
              />
            )}
          </Stack>
        </Box>
      )}

      {/* Popular Tags */}
      {!tagFilter && !categoryFilter && allTags.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalOffer fontSize="small" /> Browse by Tag
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onClick={() => handleTagClick(tag)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Articles by Category */}
      {loading ? (
        <Typography>Loading articles...</Typography>
      ) : filteredArticles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No articles found
          </Typography>
          {(tagFilter || categoryFilter || searchQuery) && (
            <Chip label="Clear filters" onClick={clearFilters} sx={{ mt: 2 }} />
          )}
        </Box>
      ) : (
        Object.entries(articlesByCategory).map(([category, categoryArticles]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {category}
            </Typography>
            <Grid container spacing={2}>
              {categoryArticles.map((article) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={article.id}>
                  <Card
                    sx={{
                      height: '100%',
                      '&:hover': { boxShadow: 3 },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/p/${organization?.slug}/kb/${article.slug}`)}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      <CardContent sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <ArticleIcon color="primary" sx={{ mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {article.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {article.excerpt || article.content.slice(0, 100)}
                            </Typography>
                            {article.tags?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                {article.tags.slice(0, 3).map((tag) => (
                                  <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTagClick(tag);
                                    }}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Box>
                          <ChevronRight color="action" />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* Tag URL Examples (for developers) */}
      <Box sx={{ mt: 6, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Link directly to tagged articles:
        </Typography>
        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
          {window.location.origin}/p/{organization?.slug}/kb?tag=getting-started
        </Typography>
      </Box>
    </Box>
  );
};
